<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '../../../../components/Modal.vue';
import PipelineForm from './PipelineForm.vue';
import { default as FluentIcon } from 'shared/components/FluentIcon/Icon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['switch-view', 'pipeline-created']);
const { t } = useI18n();
const showNewPipelineModal = ref(false);
const showTemplatesModal = ref(false);
const selectedTemplate = ref(null);
const currentTemplateIndex = ref(0);

// Computed para template atual no carrossel.
// Note: pipelineTemplates está declarado abaixo (linha ~126) — Vue
// composables permitem este forward reference porque o computed só
// avalia depois do setup completar.
/* eslint-disable no-use-before-define */
const currentTemplate = computed(
  () => pipelineTemplates[currentTemplateIndex.value]
);

const nextTemplate = () => {
  currentTemplateIndex.value =
    (currentTemplateIndex.value + 1) % pipelineTemplates.length;
};

const prevTemplate = () => {
  currentTemplateIndex.value =
    (currentTemplateIndex.value - 1 + pipelineTemplates.length) %
    pipelineTemplates.length;
};
/* eslint-enable no-use-before-define */

const goToTemplate = index => {
  currentTemplateIndex.value = index;
};

// Função para obter ícone do estágio (prioriza ícone salvo)
const getStageIcon = stage => {
  // Primeiro verifica se o estágio tem um ícone salvo
  if (stage.icon) {
    return stage.icon;
  }

  // Fallback: inferir pelo nome do estágio (para templates pré-definidos)
  const name = (stage.name || '').toLowerCase();
  // Vendas
  if (
    name.includes('prospec') ||
    name.includes('busca') ||
    name.includes('captação') ||
    name.includes('captacao')
  )
    return 'person-add';
  if (
    name.includes('qualific') ||
    name.includes('análise') ||
    name.includes('analise')
  )
    return 'checkmark-circle';
  if (name.includes('proposta') || name.includes('orçamento'))
    return 'document';
  if (name.includes('fechamento') || name.includes('contrato'))
    return 'heart-handshake';
  // Suporte
  if (name.includes('novo') || name.includes('entrada')) return 'add-circle';
  if (name.includes('desenvolvimento')) return 'code';
  if (name.includes('teste')) return 'flash-on';
  if (name.includes('resolvido') || name.includes('finalizado'))
    return 'checkmark';
  // Recrutamento
  if (name.includes('triagem')) return 'filter';
  if (name.includes('entrevista')) return 'people';
  // Imobiliário
  if (name.includes('visita')) return 'location';
  if (name.includes('documentação') || name.includes('documentacao'))
    return 'book-copy';
  if (name.includes('negociação') || name.includes('negociacao'))
    return 'credit-card-person';
  // Onboarding
  if (name.includes('boas') || name.includes('vindas')) return 'emoji';
  if (
    name.includes('configuração') ||
    name.includes('configuracao') ||
    name.includes('setup')
  )
    return 'settings';
  if (name.includes('treinamento') || name.includes('capacitação'))
    return 'book';
  if (name.includes('ativação') || name.includes('ativacao')) return 'send';
  // Marketing
  if (
    name.includes('consciência') ||
    name.includes('consciencia') ||
    name.includes('awareness')
  )
    return 'eye-show';
  if (name.includes('interesse')) return 'heart';
  if (name.includes('consideração') || name.includes('consideracao'))
    return 'search';
  if (name.includes('intenção') || name.includes('intencao')) return 'flame';
  if (name.includes('conversão') || name.includes('conversao'))
    return 'star-emphasis';
  return 'arrow-right';
};

// Função para obter cor de fundo do ícone
const getStageIconBg = color => {
  return `${color}20`;
};

// Adicione um template vazio padrão
const emptyTemplate = {
  name: '',
  description: '',
  stages: {},
  settings: {
    agents: [],
  },
};

// Templates de funis pré-definidos
const pipelineTemplates = [
  {
    id: 'sales',
    name: 'Funil de Vendas',
    icon: 'briefcase',
    description: 'Processo de vendas desde o primeiro contato até o fechamento',
    stages: {
      prospeccao: {
        name: 'Prospecção',
        color: '#6366f1',
        position: 1,
        description: 'Leads e primeiros contatos',
        message_templates: [
          {
            id: `template_${Date.now()}_1`,
            title: 'Boas vindas',
            content: 'Olá! Obrigado por entrar em contato. Como posso ajudar?',
            webhook: {
              url: '',
              method: 'POST',
              enabled: false,
            },
            stage_id: 'prospeccao',
            conditions: {
              rules: [],
              enabled: false,
            },
            created_at: new Date().toISOString(),
          },
        ],
      },
      qualificacao: {
        name: 'Qualificação',
        color: '#8b5cf6',
        position: 2,
        description: 'Análise de fit',
        message_templates: [
          {
            id: `template_${Date.now()}_2`,
            title: 'Agendamento de Reunião',
            content:
              'Podemos agendar uma reunião para entender melhor suas necessidades?',
            webhook: { url: '', method: 'POST', enabled: false },
            stage_id: 'qualificacao',
            conditions: { rules: [], enabled: false },
            created_at: new Date().toISOString(),
          },
        ],
      },
      proposta: {
        name: 'Proposta',
        color: '#ec4899',
        position: 3,
        description: 'Envio e negociação',
      },
      fechamento: {
        name: 'Fechamento',
        color: '#10b981',
        position: 4,
        description: 'Contratos e pagamentos',
      },
    },
  },
  {
    id: 'support',
    name: 'Funil de Suporte',
    icon: 'chat',
    description: 'Atendimento e resolução de tickets de suporte',
    stages: {
      novo: {
        name: 'Novo',
        color: '#6366f1',
        position: 1,
        description: 'Tickets recém abertos',
        message_templates: [
          {
            id: `template_${Date.now()}_3`,
            title: 'Confirmação de Recebimento',
            content: 'Recebemos seu ticket. Nossa equipe já está analisando.',
            webhook: { url: '', method: 'POST', enabled: false },
            stage_id: 'novo',
            conditions: { rules: [], enabled: false },
            created_at: new Date().toISOString(),
          },
        ],
      },
      analise: {
        name: 'Em Análise',
        color: '#8b5cf6',
        position: 2,
        description: 'Avaliação técnica',
        message_templates: [
          {
            id: `template_${Date.now()}_4`,
            title: 'Atualização de Status',
            content: 'Seu ticket está em análise pela nossa equipe técnica.',
            webhook: { url: '', method: 'POST', enabled: false },
            stage_id: 'analise',
            conditions: { rules: [], enabled: false },
            created_at: new Date().toISOString(),
          },
        ],
      },
      desenvolvimento: {
        name: 'Em Desenvolvimento',
        color: '#ec4899',
        position: 3,
        description: 'Correção em andamento',
      },
      teste: {
        name: 'Em Teste',
        color: '#f59e0b',
        position: 4,
        description: 'Validação da solução',
      },
      resolvido: {
        name: 'Resolvido',
        color: '#10b981',
        position: 5,
        description: 'Ticket finalizado',
      },
    },
  },
  {
    id: 'recruitment',
    name: 'Funil de Recrutamento',
    icon: 'people',
    description: 'Processo seletivo e contratação',
    stages: {
      triagem: {
        name: 'Triagem',
        color: '#6366f1',
        position: 1,
        description: 'Análise de currículos',
      },
      entrevista: {
        name: 'Entrevista',
        color: '#8b5cf6',
        position: 2,
        description: 'Conversa inicial',
      },
      teste: {
        name: 'Teste Técnico',
        color: '#ec4899',
        position: 3,
        description: 'Avaliação prática',
      },
      proposta: {
        name: 'Proposta',
        color: '#10b981',
        position: 4,
        description: 'Negociação final',
      },
    },
  },
  {
    id: 'realestate',
    name: 'Funil Imobiliário',
    icon: 'home',
    description: 'Gestão de leads e vendas de imóveis',
    stages: {
      captacao: {
        name: 'Captação',
        color: '#6366f1',
        position: 1,
        description: 'Novos interessados',
      },
      visita: {
        name: 'Visita Agendada',
        color: '#8b5cf6',
        position: 2,
        description: 'Visitas ao imóvel',
      },
      negociacao: {
        name: 'Negociação',
        color: '#f59e0b',
        position: 3,
        description: 'Propostas e contrapropostas',
      },
      documentacao: {
        name: 'Documentação',
        color: '#ec4899',
        position: 4,
        description: 'Análise de documentos',
      },
      fechamento: {
        name: 'Fechamento',
        color: '#10b981',
        position: 5,
        description: 'Contrato assinado',
      },
    },
  },
  {
    id: 'onboarding',
    name: 'Funil de Onboarding',
    icon: 'send',
    description: 'Integração e ativação de novos clientes',
    stages: {
      boasvindas: {
        name: 'Boas Vindas',
        color: '#6366f1',
        position: 1,
        description: 'Primeiro contato pós-venda',
      },
      configuracao: {
        name: 'Configuração',
        color: '#8b5cf6',
        position: 2,
        description: 'Setup inicial do produto',
      },
      treinamento: {
        name: 'Treinamento',
        color: '#ec4899',
        position: 3,
        description: 'Capacitação do cliente',
      },
      ativacao: {
        name: 'Ativação',
        color: '#10b981',
        position: 4,
        description: 'Cliente ativo e usando',
      },
    },
  },
  {
    id: 'marketing',
    name: 'Funil de Marketing',
    icon: 'megaphone',
    description: 'Campanhas e geração de leads qualificados',
    stages: {
      awareness: {
        name: 'Consciência',
        color: '#6366f1',
        position: 1,
        description: 'Primeiro contato com a marca',
      },
      interesse: {
        name: 'Interesse',
        color: '#8b5cf6',
        position: 2,
        description: 'Demonstrou interesse',
      },
      consideracao: {
        name: 'Consideração',
        color: '#f59e0b',
        position: 3,
        description: 'Avaliando soluções',
      },
      intencao: {
        name: 'Intenção',
        color: '#ec4899',
        position: 4,
        description: 'Pronto para comprar',
      },
      conversao: {
        name: 'Conversão',
        color: '#10b981',
        position: 5,
        description: 'Lead convertido',
      },
    },
  },
];

const handleTemplateSelect = template => {
  // Formata o template igual ao formato de edição
  const formattedTemplate = {
    name: template.name,
    description: template.description,
    stages: Object.entries(template.stages).reduce(
      (acc, [id, stage]) => ({
        ...acc,
        [id]: {
          ...stage,
          id,
          message_templates: stage.message_templates || [], // Preserva os templates existentes
          position: stage.position, // Garante que a posição é mantida
        },
      }),
      {}
    ),
    settings: {
      agents: [],
    },
  };

  // Ordena as etapas por posição
  const orderedStages = {};
  Object.entries(formattedTemplate.stages)
    .sort(([, a], [, b]) => a.position - b.position)
    .forEach(([id, stage]) => {
      orderedStages[id] = stage;
    });

  formattedTemplate.stages = orderedStages;

  // Abre o modal com os dados formatados
  selectedTemplate.value = formattedTemplate;
  showTemplatesModal.value = false;
  showNewPipelineModal.value = true;
};

const handleBack = () => {
  /* eslint-disable-next-line vue/custom-event-name-casing -- consumers bind via @switch-view in template */
  emit('switch-view', 'pipeline');
};

const handlePipelineCreated = pipeline => {
  showNewPipelineModal.value = false;
  /* eslint-disable-next-line vue/custom-event-name-casing -- consumers bind via @pipeline-created in template */
  emit('pipeline-created', pipeline);
};
</script>

<template>
  <header
    class="h-14 px-4 flex items-center justify-between border-b border-n-weak bg-n-background flex-shrink-0"
  >
    <!-- Left: Back + Title -->
    <div class="flex items-center gap-2">
      <button
        class="p-1.5 rounded-lg hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12 transition-colors"
        @click="handleBack"
      >
        <FluentIcon
          icon="arrow-chevron-left"
          size="14"
          :icons="dashboardIcons"
        />
      </button>
      <h1 class="text-sm font-semibold text-n-slate-12">
        {{ t('PIPELINE.PIPELINES.TITLE') }}
      </h1>
    </div>

    <!-- Right: Actions -->
    <div class="flex items-center gap-1.5">
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
        @click="showTemplatesModal = true"
      >
        <FluentIcon icon="copy" size="14" :icons="dashboardIcons" />
        <span>{{ $t('PIPELINE.TEMPLATES_LIBRARY.TRIGGER_LABEL') }}</span>
      </button>
      <Button
        variant="solid"
        color="blue"
        size="xs"
        @click="showNewPipelineModal = true"
      >
        <template #icon>
          <FluentIcon icon="add" size="14" :icons="dashboardIcons" />
        </template>
        <span>{{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}</span>
      </Button>
    </div>

    <!-- Modal de Templates - Novo Design Carrossel -->
    <Modal
      v-model:show="showTemplatesModal"
      class="templates-modal"
      :show-close-button="false"
      @close="() => (showTemplatesModal = false)"
    >
      <div class="flex flex-col h-full">
        <!-- Header -->
        <div class="p-6 pb-4">
          <div class="flex justify-between items-start">
            <div>
              <h2 class="text-heading-1 font-bold text-n-slate-12">
                {{ $t('PIPELINE.TEMPLATES_LIBRARY.TITLE') }}
              </h2>
              <p class="text-body-main text-n-slate-10 mt-1">
                {{ $t('PIPELINE.TEMPLATES_LIBRARY.SUBTITLE') }}
              </p>
            </div>
            <button
              class="text-n-slate-9 hover:text-n-slate-12 p-1"
              @click="showTemplatesModal = false"
            >
              <FluentIcon icon="dismiss" size="24" :icons="dashboardIcons" />
            </button>
          </div>
        </div>

        <!-- Carrossel Content -->
        <div class="flex-1 relative px-6">
          <!-- Botão Anterior -->
          <button
            class="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-n-solid-2 shadow-md hover:bg-n-alpha-1 text-n-slate-11"
            @click="prevTemplate"
          >
            <FluentIcon icon="chevron-left" size="20" :icons="dashboardIcons" />
          </button>

          <!-- Template Atual -->
          <div class="text-center px-8 py-4">
            <h3 class="text-heading-2 font-bold text-n-slate-12 mb-2">
              {{ currentTemplate.name }}
            </h3>
            <p class="text-body-main text-n-slate-10 max-w-2xl mx-auto mb-6">
              {{ currentTemplate.description }}
            </p>

            <!-- Grid de Estágios -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div
                v-for="stage in Object.values(currentTemplate.stages).sort(
                  (a, b) => a.position - b.position
                )"
                :key="stage.name"
                class="bg-n-solid-2 p-4 rounded-xl border border-n-weak flex flex-col items-center text-center"
              >
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                  :style="{ backgroundColor: getStageIconBg(stage.color) }"
                >
                  <FluentIcon
                    :icon="getStageIcon(stage)"
                    size="20"
                    :icons="dashboardIcons"
                    :style="{ color: stage.color }"
                  />
                </div>
                <h4 class="text-label text-n-slate-11">
                  {{ stage.name }}
                </h4>
              </div>
            </div>
          </div>

          <!-- Botão Próximo -->
          <button
            class="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-n-solid-2 shadow-md hover:bg-n-alpha-1 text-n-slate-11"
            @click="nextTemplate"
          >
            <FluentIcon
              icon="chevron-right"
              size="20"
              :icons="dashboardIcons"
            />
          </button>
        </div>

        <!-- Footer -->
        <div
          class="p-6 pt-4 bg-n-solid-2 border-t border-n-weak flex justify-between items-center"
        >
          <!-- Indicadores de Paginação -->
          <div class="flex items-center gap-1.5">
            <button
              v-for="(_, index) in pipelineTemplates"
              :key="index"
              class="rounded-full transition-all"
              :class="
                index === currentTemplateIndex
                  ? 'w-2.5 h-2.5 bg-n-brand'
                  : 'w-2 h-2 bg-n-weak hover:bg-n-strong'
              "
              @click="goToTemplate(index)"
            />
          </div>

          <!-- Botão de Ação -->
          <Button
            variant="solid"
            color="blue"
            size="sm"
            @click="handleTemplateSelect(currentTemplate)"
          >
            <span>{{ $t('PIPELINE.TEMPLATES_LIBRARY.USE_TEMPLATE') }}</span>
            <template #icon>
              <FluentIcon
                icon="arrow-right"
                size="16"
                :icons="dashboardIcons"
              />
            </template>
          </Button>
        </div>
      </div>
    </Modal>

    <!-- Modal de Novo Funil -->
    <Modal
      v-model:show="showNewPipelineModal"
      class="pipeline-modal"
      @close="() => (showNewPipelineModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
        </h3>
        <PipelineForm
          :initial-data="selectedTemplate || emptyTemplate"
          @saved="handlePipelineCreated"
          @close="showNewPipelineModal = false"
        />
      </div>
    </Modal>
  </header>
</template>

<style scoped>
/* Modal deep styling */
:deep(.pipeline-modal .modal-container) {
  max-width: 1024px;
  width: 90vw;
}

:deep(.pipeline-modal .modal-container) {
  background-color: var(--color-n-background);
}

:deep(.pipeline-modal .modal-container h3) {
  color: var(--color-n-slate-12);
}

:deep(.templates-modal .modal-container) {
  max-width: 800px;
  width: 90vw;
  overflow: hidden;
}

:deep(.templates-modal .modal-container) {
  background-color: var(--color-n-background);
}
</style>
